import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AbntPipe } from '../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { RelatorioContasPagarService } from '../../../../shared/services/apis/financas/relatorio-contas-pagar.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { PessoaLookupService } from '../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { RelatorioContasPagarRequest } from '../../../../shared/services/requests/financas/relatorio-contas-pagar.request';
import { SaveRelatorioFiltroFavoritoRequest } from '../../../../shared/services/requests/financas/save-relatorio-filtro-favorito.request';
import { RelatorioFiltroFavorito } from '../../../../shared/services/responses/financas/relatorio-filtro-favorito.response';
import {
  CategoriaRelatorioItem,
  RelatorioContasPagarItem,
  RelatorioContasPagarGrupo,
} from '../../../../shared/services/responses/financas/relatorio-contas-pagar.response';
import { FinancasLookupItem } from '../../../../shared/services/responses/financas/lookup-online-select.response';
import { NomeFavoritoModalComponent } from '../relatorio-entradas-saidas-cliente/components/nome-favorito-modal/nome-favorito-modal.component';

type ModoPeriodo = 'mes' | 'intervalo';
@Component({
  selector: 'app-relatorio-contas-pagar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    HeaderComponent,
    EmptyListComponent,
    LoadingBoxComponent,
    AbntPipe,
  ],
  providers: [DatePipe],
  templateUrl: './relatorio-contas-pagar.component.html',
  styleUrl: './relatorio-contas-pagar.component.css',
})
export class RelatorioContasPagarComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Relatorios;

  public readonly periodoOpcoes = [
    { id: 'mes' as const, descricao: 'do mês' },
    { id: 'intervalo' as const, descricao: 'intervalo' },
  ];

  public situacaoOpcoes: FinancasLookupItem[] = [];

  public readonly agrupamentoOpcoes = [
    { id: 'nenhum', descricao: 'Não agrupar' },
    { id: 'vencimento', descricao: 'Data de vencimento' },
    { id: 'fornecedor', descricao: 'Fornecedor' },
    { id: 'categoria', descricao: 'Categoria' },
  ];

  public form = this.fb.group({
    modoPeriodo: ['mes' as ModoPeriodo, Validators.required],
    mesAno: [this.getMesAnoAtual(), Validators.required],
    dataInicial: [null as Date | null],
    dataFinal: [null as Date | null],
    situacoes: [[] as number[]],
    agrupamento: ['nenhum'],
    idCategoriaFinanceira: [null as number | null],
    favoritoSelecionado: [null as string | null],
  });

  public carregandoLookups = true;
  public carregandoRelatorio = false;
  public filtrosVisiveis = true;
  public consultaGerada = false;
  public possuiResultados = false;
  public mensagemEmptyState = '';

  public categorias: CategoriaRelatorioItem[] = [];
  public favoritos: RelatorioFiltroFavorito[] = [];
  public listaItens: RelatorioContasPagarItem[] = [];
  public listaGrupos: RelatorioContasPagarGrupo[] = [];
  public totalValor = 0;
  public totalSaldo = 0;
  public totalPago = 0;
  public agrupamentoAtual = 'nenhum';

  private readonly destroy$ = new Subject<void>();
  private readonly codigoRelatorio = 'CONTAS_A_PAGAR';
  private readonly favoritosStorageKey = `financas-${this.codigoRelatorio.toLowerCase()}-favoritos`;
  private readonly idTipoRelatorioState = Number(window.history.state?.idTipoRelatorio || 0) || null;
  private idTipoRelatorio: number | null = null;
  private periodoDescricaoGerado = '';

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private relatorioService: RelatorioContasPagarService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private pessoaLookupService: PessoaLookupService
  ) {}

  public ngOnInit(): void {
    this.carregarLookups();
    this.configurarMudancaPeriodo();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public voltar(): void {
    this.router.navigate(['/financas/relatorios']);
  }

  public toggleFiltros(): void {
    this.filtrosVisiveis = !this.filtrosVisiveis;
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  public getIconoAgrupamento(): string {
    switch (this.agrupamentoAtual) {
      case 'vencimento':
        return 'event';
      case 'fornecedor':
        return 'storefront';
      case 'categoria':
        return 'category';
      default:
        return 'view_agenda';
    }
  }

  public get isModoMes(): boolean {
    return this.form.get('modoPeriodo')?.value === 'mes';
  }

  public get mesAnoControl(): FormControl<string> {
    return this.form.get('mesAno') as FormControl<string>;
  }

  public get dataInicialControl(): FormControl<Date | null> {
    return this.form.get('dataInicial') as FormControl<Date | null>;
  }

  public get dataFinalControl(): FormControl<Date | null> {
    return this.form.get('dataFinal') as FormControl<Date | null>;
  }

  public get hasFavoritoSelecionado(): boolean {
    return !!this.favoritoSelecionadoAtual;
  }

  public get hasAlgumFiltroPreenchido(): boolean {
    const situacoes = this.form.get('situacoes')?.value ?? [];
    const agrupamento = this.form.get('agrupamento')?.value ?? 'nenhum';
    const idCategoria = this.form.get('idCategoriaFinanceira')?.value ?? null;
    if (situacoes.length || agrupamento !== 'nenhum' || idCategoria) return true;
    if (this.form.get('modoPeriodo')?.value === 'intervalo') {
      return !!(this.form.get('dataInicial')?.value || this.form.get('dataFinal')?.value);
    }
    return false;
  }

  public get favoritoSelecionadoAtual(): RelatorioFiltroFavorito | null {
    const id = this.form.get('favoritoSelecionado')?.value;
    return this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id) || null;
  }

  // ─── Categoria helpers ─────────────────────────────────────────────────────

  public getCategoriaLabel(item: CategoriaRelatorioItem): string {
    return '\u00A0\u00A0'.repeat(item.nivel) + item.descricao;
  }

  // ─── Period ────────────────────────────────────────────────────────────────

  public selecionarModoPeriodo(modo: ModoPeriodo): void {
    this.form.patchValue({ modoPeriodo: modo });
  }

  public onDateInput(event: Event, controlName: 'dataInicial' | 'dataFinal'): void {
    MaskService.onDateInput(event, this.form.get(controlName));
  }

  public navegarMes(passos: number): void {
    const ref = this.parseMesAno(this.mesAnoControl.value) ?? new Date();
    ref.setMonth(ref.getMonth() + passos);
    this.mesAnoControl.setValue(this.formatMesAno(ref));
  }

  public normalizarMesAno(): void {
    const norm = this.parseMesAno(this.mesAnoControl.value);
    this.mesAnoControl.setValue(norm ? this.formatMesAno(norm) : this.getMesAnoAtual());
  }

  // ─── Favorites ─────────────────────────────────────────────────────────────

  public favoritarFiltros(): void {
    if (this.hasFavoritoSelecionado) {
      this.removerFavoritoSelecionado();
      return;
    }

    const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: false,
      data: { nomeInicial: this.getProximoNomeFavorito() },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((nome: string | undefined) => {
        if (!nome?.trim()) return;
        this.salvarFavorito(nome.trim());
      });
  }

  public carregarFavoritoSelecionado(): void {
    const id = this.form.get('favoritoSelecionado')?.value;
    if (!id) {
      this.limparFiltrosFavorito();
      return;
    }

    const favorito = this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id);
    if (!favorito) {
      this.limparFiltrosFavorito();
      return;
    }

    this.form.patchValue({
      modoPeriodo: (favorito.filtro.modoPeriodo === 'intervalo' ? 'intervalo' : 'mes') as ModoPeriodo,
      mesAno: favorito.filtro.mesAno ?? this.getMesAnoAtual(),
      dataInicial: favorito.filtro.dataInicial ? new Date(favorito.filtro.dataInicial) : null,
      dataFinal: favorito.filtro.dataFinal ? new Date(favorito.filtro.dataFinal) : null,
      situacoes: favorito.filtro.situacoes ?? [],
      idCategoriaFinanceira: favorito.filtro.idCategoriaFinanceira,
    });
  }

  public getFavoritoValue(favorito: RelatorioFiltroFavorito): string {
    return String(favorito.idRelatorioFiltroFavorito);
  }

  // ─── Lookup ────────────────────────────────────────────────────────────────

  public carregarLookups(): void {
    this.carregandoLookups = true;
    this.relatorioService
      .getLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.categorias = response.listaCategorias || [];
          this.situacaoOpcoes = response.listaSituacoes || [];
          this.idTipoRelatorio = response.idTipoRelatorio ?? this.idTipoRelatorioState;
          this.favoritos = response.listaFavoritos || [];
          if (!this.idTipoRelatorio) this.carregarFavoritosLocalStorage();
          this.carregandoLookups = false;
        },
        error: () => {
          this.carregandoLookups = false;
          this.alertService.show('Erro', 'Erro ao carregar os filtros do relatório.', AlertType.error);
        },
      });
  }

  // ─── Generate ──────────────────────────────────────────────────────────────

  public gerar(): void {
    if (!this.validarPeriodo()) return;

    const { inicio, fim } = this.getPeriodoSelecionado();
    if (!inicio || !fim) {
      this.alertService.show(
        'Atenção',
        'Informe um período válido para gerar o relatório.',
        AlertType.warning
      );
      return;
    }

    this.periodoDescricaoGerado = this.getPeriodoDescricao();
    this.consultaGerada = true;
    this.carregandoRelatorio = true;
    this.mensagemEmptyState = '';

    const request: RelatorioContasPagarRequest = {
      dataInicio: this.datePipe.transform(inicio, 'yyyy-MM-dd') || '',
      dataFim: this.datePipe.transform(fim, 'yyyy-MM-dd') || '',
      situacoes: this.form.get('situacoes')?.value ?? [],
      agrupamento: this.form.get('agrupamento')?.value || 'nenhum',
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
    };

    this.relatorioService
      .getPreview(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.agrupamentoAtual = response.agrupamento || 'nenhum';
          this.listaItens = (response.listaItens || []).map(i => ({
            ...i,
            valor: Number(i.valor || 0),
            saldo: Number(i.saldo || 0),
            pago: Number(i.pago || 0),
          }));
          this.listaGrupos = (response.listaGrupos || []).map(g => ({
            ...g,
            subtotalValor: Number(g.subtotalValor || 0),
            subtotalSaldo: Number(g.subtotalSaldo || 0),
            subtotalPago: Number(g.subtotalPago || 0),
            itens: (g.itens || []).map(i => ({
              ...i,
              valor: Number(i.valor || 0),
              saldo: Number(i.saldo || 0),
              pago: Number(i.pago || 0),
            })),
          }));
          this.totalValor = Number(response.totalValor || 0);
          this.totalSaldo = Number(response.totalSaldo || 0);
          this.totalPago = Number(response.totalPago || 0);
          this.aplicarNomesFornecedores();
        },
        error: () => {
          this.listaItens = [];
          this.listaGrupos = [];
          this.totalValor = 0;
          this.totalSaldo = 0;
          this.totalPago = 0;
          this.possuiResultados = false;
          this.filtrosVisiveis = true;
          this.carregandoRelatorio = false;
          this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
        },
      });
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  public imprimir(): void {
    if (!this.possuiResultados) return;

    const esc = (v: string | null | undefined) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const periodo = this.periodoDescricaoGerado;
    const geradoEm = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') || '';
    const empresa = this.authService.getUserInfos().nomeEmpresa;

    const buildRows = (itens: RelatorioContasPagarItem[]) =>
      itens
        .map(
          item => `
            <tr${item.isAtrasada ? ' class="atrasada"' : ''}>
                <td>${esc(item.fornecedor)}</td>
                <td>${esc(item.historico)}</td>
                <td>${esc(item.categoria)}</td>
                <td>${esc(item.nroDocumento)}</td>
                <td>${this.formatarData(item.dataEmissao)}</td>
                <td>${this.formatarData(item.dataVencimento)}</td>
                <td class="valor">${this.formatarMoeda(item.valor)}</td>
                <td class="valor">${this.formatarMoeda(item.saldo)}</td>
                <td class="valor">${this.formatarMoeda(item.pago)}</td>
            </tr>`
        )
        .join('');

    const thead = `<thead><tr>
            <th>Fornecedor</th><th>Descrição</th><th>Categoria</th>
            <th>Nº Doc.</th><th>Emissão</th><th>Vencimento</th>
            <th class="valor">Valor</th><th class="valor">Saldo</th><th class="valor">Pago</th>
        </tr></thead>`;

    let tablesHtml = '';

    if (this.agrupamentoAtual === 'nenhum') {
      tablesHtml = `<table>${thead}<tbody>${buildRows(this.listaItens)}</tbody></table>`;
    } else {
      tablesHtml = this.listaGrupos
        .map(
          g => `
                <section class="grupo">
                    <h2>${esc(g.descricaoGrupo)}</h2>
                    <table>${thead}<tbody>${buildRows(g.itens)}</tbody></table>
                </section>`
        )
        .join('');
    }

    const conteudo = `<html>
        <head>
            <title>Relatório de Contas a Pagar</title>
            <style>
                body { font-family: Arial, sans-serif; color: #1f2937; padding: 24px; font-size: 12px; }
                h1 { margin: 0 0 8px; font-size: 20px; }
                .meta { margin-bottom: 20px; color: #475569; font-size: 11px; }
                .grupo { margin-bottom: 24px; page-break-inside: avoid; }
                .grupo h2 { font-size: 14px; margin: 0 0 8px; color: #374151; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
                thead { display: table-header-group; }
                th, td { border-bottom: 1px solid #e5e7eb; padding: 7px 6px; text-align: left; }
                th.valor, td.valor { text-align: right; }
                .atrasada td { color: #b73232; }
                @media print { @page { margin: 1.5cm; } }
            </style>
        </head>
        <body>
            <h1>Relatório de Contas a Pagar</h1>
            <div class="meta">
                <div>Período: ${periodo}</div>
                <div>Gerado em: ${geradoEm}</div>
                <div>Empresa: ${empresa}</div>
            </div>
            ${tablesHtml}
            <div style="margin-top:16px;font-weight:700;text-align:right;">
                Total Valor: ${this.formatarMoeda(this.totalValor)} &nbsp;|&nbsp;
                Total Saldo: ${this.formatarMoeda(this.totalSaldo)} &nbsp;|&nbsp;
                Total Pago: ${this.formatarMoeda(this.totalPago)}
            </div>
        </body>
        </html>`;

    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) return;
    popup.document.write(conteudo);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  public downloadCsv(): void {
    if (!this.possuiResultados) return;

    const sep = ';';
    // pt-BR (vírgula decimal) — consistente com o separador ';' do CSV; .toFixed() usaria ponto e o
    // Excel-BR trataria a coluna como texto, quebrando fórmulas. Reaproveita o AbntPipe já usado no app
    // (mesmo arredondamento ABNT do resto do sistema).
    const abntPipe = new AbntPipe(this.authService);
    const formatarNumero = (valor: number) => abntPipe.transform(valor, 'quantidade', 2);

    const header = [
      'Vencimento',
      'Fornecedor',
      'Descrição',
      'Nº Documento',
      'Data de Emissão',
      'Valor (R$)',
      'Saldo (R$)',
      'Pago (R$)',
    ].join(sep);

    const toRow = (item: RelatorioContasPagarItem) =>
      [
        this.formatarData(item.dataVencimento),
        item.fornecedor,
        item.historico,
        item.nroDocumento ?? '',
        this.formatarData(item.dataEmissao),
        formatarNumero(item.valor),
        formatarNumero(item.saldo),
        formatarNumero(item.pago),
      ].join(sep);

    const itens =
      this.agrupamentoAtual === 'nenhum' ? this.listaItens : this.listaGrupos.flatMap(g => g.itens);

    const csv = '\uFEFF' + [header, ...itens.map(toRow)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'relatorio-contas-a-pagar.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  // ─── Resolução de nome real do Fornecedor ───────────────────────────────────
  // O backend expõe "fornecedor" como descricaoOriginal do documento (texto livre do lançamento), não o
  // nome cadastral da Pessoa — mesma causa-raiz já corrigida em impressao-contas-pagar-dialog.component.ts.
  // Resolve aqui via PessoaLookupService, sobrescrevendo tanto o texto de cada linha (listaItens e dentro
  // de cada grupo) quanto o cabeçalho de cada grupo quando o agrupamento é por Fornecedor.

  private aplicarNomesFornecedores(): void {
    const idsFornecedores = Array.from(
      new Set([
        ...this.listaItens.map(i => i.idPessoaFornecedor),
        ...this.listaGrupos.flatMap(g => g.itens.map(i => i.idPessoaFornecedor)),
        ...this.listaGrupos.map(g => g.idPessoaFornecedor ?? 0),
      ].filter(id => !!id && id > 0))
    );

    const finalizar = (nomesPorId: Map<number, string>): void => {
      const resolverNome = (idPessoaFornecedor: number, textoAtual: string): string =>
        idPessoaFornecedor > 0 ? nomesPorId.get(idPessoaFornecedor) || textoAtual : textoAtual;

      this.listaItens = this.listaItens.map(item => ({
        ...item,
        fornecedor: resolverNome(item.idPessoaFornecedor, item.fornecedor),
      }));

      this.listaGrupos = this.listaGrupos.map(grupo => ({
        ...grupo,
        descricaoGrupo:
          grupo.idPessoaFornecedor != null
            ? resolverNome(grupo.idPessoaFornecedor, grupo.descricaoGrupo)
            : grupo.descricaoGrupo,
        itens: grupo.itens.map(item => ({
          ...item,
          fornecedor: resolverNome(item.idPessoaFornecedor, item.fornecedor),
        })),
      }));

      this.possuiResultados = this.listaItens.length > 0 || this.listaGrupos.length > 0;
      this.filtrosVisiveis = !this.possuiResultados;
      this.mensagemEmptyState = this.possuiResultados
        ? ''
        : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
      this.carregandoRelatorio = false;
    };

    if (!idsFornecedores.length) {
      finalizar(new Map());
      return;
    }

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = idsFornecedores;

    this.pessoaLookupService
      .getListPessoaIds(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            finalizar(new Map());
            return;
          }
          const nomesPorId = new Map(
            (response.listaPessoa || [])
              .filter(item => !!item.idPessoa && !!item.nome?.trim())
              .map(item => [item.idPessoa, item.nome.trim()])
          );
          finalizar(nomesPorId);
        },
        error: () => finalizar(new Map()),
      });
  }

  // ─── Formatting ────────────────────────────────────────────────────────────

  public formatarData(value: string | null | undefined): string {
    if (!value) return '—';
    const normalizado = String(value).includes('T') ? value : `${value}T00:00:00`;
    return this.datePipe.transform(normalizado, 'dd/MM/yyyy') || '—';
  }

  public formatarMoeda(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  public getPeriodoDescricao(): string {
    if (this.isModoMes) return this.mesAnoControl.value;
    const inicio = this.dataInicialControl.value
      ? this.datePipe.transform(this.dataInicialControl.value, 'dd/MM/yyyy')
      : '';
    const fim = this.dataFinalControl.value
      ? this.datePipe.transform(this.dataFinalControl.value, 'dd/MM/yyyy')
      : '';
    return `${inicio} a ${fim}`;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private configurarMudancaPeriodo(): void {
    this.form
      .get('modoPeriodo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(modo => {
        if (!modo) return;
        if (modo === 'mes') {
          this.dataInicialControl.setValue(null);
          this.dataFinalControl.setValue(null);
        } else {
          this.mesAnoControl.setValue(this.getMesAnoAtual());
        }
      });
  }

  private validarPeriodo(): boolean {
    if (this.isModoMes) {
      if (!this.parseMesAno(this.mesAnoControl.value)) {
        this.alertService.show('Atenção', 'Informe um mês/ano válido no formato MM/AAAA.', AlertType.warning);
        return false;
      }
      return true;
    }
    const ini = this.dataInicialControl.value;
    const fim = this.dataFinalControl.value;
    if (!ini || !fim) {
      this.alertService.show('Atenção', 'Informe a data inicial e a data final.', AlertType.warning);
      return false;
    }
    if (fim < ini) {
      this.alertService.show(
        'Atenção',
        'A data final não pode ser anterior à data inicial.',
        AlertType.warning
      );
      return false;
    }
    return true;
  }

  private getPeriodoSelecionado(): { inicio: Date | null; fim: Date | null } {
    if (this.isModoMes) {
      const ref = this.parseMesAno(this.mesAnoControl.value);
      if (!ref) return { inicio: null, fim: null };
      const inicio = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const fim = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
      fim.setHours(23, 59, 59, 999);
      return { inicio, fim };
    }
    const inicio = this.dataInicialControl.value ? new Date(this.dataInicialControl.value) : null;
    const fim = this.dataFinalControl.value ? new Date(this.dataFinalControl.value) : null;
    if (fim) fim.setHours(23, 59, 59, 999);
    return { inicio, fim };
  }

  private limparFiltrosFavorito(): void {
    this.form.patchValue({
      favoritoSelecionado: null,
      modoPeriodo: 'mes',
      mesAno: this.getMesAnoAtual(),
      dataInicial: null,
      dataFinal: null,
      situacoes: [],
      agrupamento: 'nenhum',
      idCategoriaFinanceira: null,
    });
  }

  private carregarFavoritosLocalStorage(): void {
    try {
      const raw = localStorage.getItem(this.favoritosStorageKey);
      this.favoritos = raw ? (JSON.parse(raw) as RelatorioFiltroFavorito[]) : [];
    } catch {
      this.favoritos = [];
    }
  }

  private persistirFavoritos(): void {
    if (this.idTipoRelatorio) return;
    localStorage.setItem(this.favoritosStorageKey, JSON.stringify(this.favoritos));
  }

  private getProximoNomeFavorito(): string {
    const numeros = this.favoritos
      .map(f => /^Favorito (\d+)$/.exec(f.nome)?.[1])
      .filter((v): v is string => !!v)
      .map(Number)
      .filter(v => !Number.isNaN(v));
    const next = numeros.length ? Math.max(...numeros) + 1 : this.favoritos.length + 1;
    return `Favorito ${next}`;
  }

  private salvarFavorito(nome: string): void {
    const filtro = {
      modoPeriodo: this.form.get('modoPeriodo')?.value || 'mes',
      mesAno: this.mesAnoControl.value,
      dataInicial: this.dataInicialControl.value
        ? this.datePipe.transform(this.dataInicialControl.value, 'yyyy-MM-dd')
        : null,
      dataFinal: this.dataFinalControl.value
        ? this.datePipe.transform(this.dataFinalControl.value, 'yyyy-MM-dd')
        : null,
      situacoes: this.form.get('situacoes')?.value ?? [],
      idCliente: null,
      clienteDescricao: null,
      idContaFinanceira: null,
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
    };

    if (!this.idTipoRelatorio) {
      const favorito = {
        idRelatorioFiltroFavorito: Date.now(),
        idTipoRelatorio: 0,
        nome,
        isPadrao: false,
        datetimeInclusao: new Date().toISOString(),
        filtro,
      } as RelatorioFiltroFavorito;
      this.favoritos = [favorito, ...this.favoritos];
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
      return;
    }

    const request: SaveRelatorioFiltroFavoritoRequest = {
      idTipoRelatorio: this.idTipoRelatorio,
      nome,
      isPadrao: false,
      filtro,
    };
    this.relatorioService
      .saveFavorito(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError || !response.favorito) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao salvar favorito.',
              AlertType.error
            );
            return;
          }
          const favorito = response.favorito;
          this.favoritos = [
            favorito,
            ...this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito),
          ].sort((a, b) => a.nome.localeCompare(b.nome));
          this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
        },
        error: () => this.alertService.show('Erro', 'Erro ao salvar favorito.', AlertType.error),
      });
  }

  private removerFavoritoSelecionado(): void {
    const favorito = this.favoritoSelecionadoAtual;
    if (!favorito) return;

    if (!this.idTipoRelatorio) {
      this.favoritos = this.favoritos.filter(
        f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
      );
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: null });
      return;
    }

    this.relatorioService
      .deleteFavorito(favorito.idRelatorioFiltroFavorito)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao remover favorito.',
              AlertType.error
            );
            return;
          }
          this.favoritos = this.favoritos.filter(
            f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
          );
          this.form.patchValue({ favoritoSelecionado: null });
        },
        error: () => this.alertService.show('Erro', 'Erro ao remover favorito.', AlertType.error),
      });
  }

  private getMesAnoAtual(): string {
    return this.formatMesAno(new Date());
  }

  private formatMesAno(date: Date): string {
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private parseMesAno(value: string | null | undefined): Date | null {
    const match = (value || '').trim().match(/^(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const mes = Number(match[1]);
    const ano = Number(match[2]);
    if (mes < 1 || mes > 12) return null;
    return new Date(ano, mes - 1, 1);
  }
}
